import { MouseEvent } from 'react';
import Button, { Size, Kind, InlineButtonGroup } from '@uds/button';
import { cssTheme } from '@uds/theme-utils';
import FadeInOut from 'presentation/components/common/FadeInOut';
import { Glyph } from '@uds/glyphs';
import { cx } from 'linaria';
import { useIntl } from 'react-intl';
import { useDSTContext } from 'presentation/components/pages/CMPage/DSTContext';

import { AvailabilityStatus } from '../../utils';
import { Kind as FeedbackKind } from '../action-feedback/constants';
import { ActionFeedback } from '../action-feedback/ActionFeedback';
import { classNames } from './PurchaseGroup.stylesheet';
import { kindClass } from '../action-feedback/ActionFeedback.stylesheet';

export interface PurchaseGroupProps {
  availability: AvailabilityStatus;
  actions: {
    handleViewBasket?: (e?: MouseEvent<HTMLElement>) => void;
    handleAddToBasket: (e?: MouseEvent<HTMLElement>) => void;
  };
  feedbackKind: FeedbackKind;
  unavailableFeedbackKind: FeedbackKind;
  buttonKind?: Kind;
  text: {
    verifyingGlyph: string;
    purchaseGlyph?: string;
    addedGlyph: string;
    basketGlyph: string;
    unavailableGlyph: string;
    addedToBasket: string;
    basket: string;
    addToBasket: string;
    unavailable: string;
  };
  verifyingButton?: 'GRAY' | 'PRIMARY_MUTED';
}

const RenderPurchaseComponent = ({
  availability,
  text,
  actions,
  feedbackKind,
  unavailableFeedbackKind,
  buttonKind = Kind.PRIMARY,
  verifyingButton = 'GRAY',
}: PurchaseGroupProps) => {
  const { disableCtaButtons } = useDSTContext();
  const { formatMessage } = useIntl();
  switch (availability) {
    case AvailabilityStatus.VERIFYING:
    case AvailabilityStatus.AVAILABLE:
      return (
        <Button
          tabIndex={-1}
          data-testid="purchase-group-loading-button"
          // @ts-ignore: hard to type when it comes fro the cms
          glyph={<Glyph alt={formatMessage({ id: 'usf.dst.v2.purchaseGroup.loading' })} name={text.verifyingGlyph} />}
          className={cx(
            classNames.pendingButton,
            verifyingButton === 'GRAY' ? classNames.customButton : kindClass.PRIMARY_MUTED,
          )}
        />
      );
    case AvailabilityStatus.IN_BASKET:
      return (
        <InlineButtonGroup>
          <ActionFeedback
            label={text.addedToBasket}
            kind={feedbackKind}
            // @ts-ignore: should be a correct string
            glyph={text.addedGlyph}
          />
          <Button
            size={Size.MD}
            onClick={actions.handleViewBasket}
            disabled={disableCtaButtons}
            glyph={
              <Glyph
                // @ts-ignore: hard to type when it comes fro the cms
                name={text.basketGlyph}
              />
            }
          >
            {text.basket}
          </Button>
        </InlineButtonGroup>
      );
    case AvailabilityStatus.UNAVAILABLE:
      return (
        <ActionFeedback
          label={text.unavailable}
          kind={unavailableFeedbackKind}
          // @ts-ignore: should be a correct string
          glyph={text.unavailableGlyph}
          glyphColor={cssTheme.palette.red01}
        />
      );

    default:
      return (
        <Button
          disabled={disableCtaButtons}
          size={Size.MD}
          kind={buttonKind}
          // @ts-ignore null not assignable
          glyph={
            text.purchaseGlyph == null ? undefined : (
              <Glyph
                // @ts-ignore: hard to type when it comes fro the cms
                name={text.purchaseGlyph}
              />
            )
          }
          onClick={actions.handleAddToBasket}
        >
          {text.addToBasket}
        </Button>
      );
  }
};

const transitionKeyMap: Record<AvailabilityStatus, string> = {
  [AvailabilityStatus.VERIFYING]: 'VERIFYING',
  [AvailabilityStatus.AVAILABLE]: 'VERIFYING',
  [AvailabilityStatus.IN_BASKET]: 'IN_BASKET',
  [AvailabilityStatus.UNAVAILABLE]: 'IN_BASKET',
  [AvailabilityStatus.UNVERIFIED]: 'UNVERIFIED',
};

const PurchaseGroup = (props: PurchaseGroupProps) => {
  return (
    <FadeInOut transitionKey={transitionKeyMap[props.availability]}>
      <RenderPurchaseComponent {...props} />
    </FadeInOut>
  );
};

export default PurchaseGroup;
